import { Analytic, GoalDirection, GoalRange, OnDemandAnalysis, TimeInGoalAnalysis, TraceGoal } from "./AnalysisDetails"
import { ColorSpectrum } from "./ColorSpectrum"
import { DataSource } from "./DataSource"
import { HeatmapTraceConfigJSON, RenderStrategy } from "./Trace"

// Goals in Data Review. Each measurement in a line graph carries its own goal (TraceGoal), set
// in the display. The values below are only the starting values offered for known vitals. Every
// calculation runs in query (the time_in_goal analytic).

export type GoalVital = {
	key: string
	label: string
	units: string
	modalities: string[] // archive labels, canonical first, cuff pressure last
	goal: GoalRange
	precision: number
}

export const GOAL_VITALS: GoalVital[] = [
	{ key: "HR", label: "HR", units: "bpm", modalities: ["HR_na"], goal: { lo: 60, hi: 110 }, precision: 0 },
	{ key: "RR", label: "RR", units: "breaths/min", modalities: ["RR_na"], goal: { lo: 12, hi: 20 }, precision: 0 },
	{ key: "MAP", label: "MAP", units: "mmHg", modalities: ["ABP_Mean", "ART_Mean", "ART1_Mean", "ART2_Mean", "ART3_Mean", "ART4_Mean", "NBP_Mean"], goal: { lo: 65, hi: 110 }, precision: 0 },
	{ key: "SBP", label: "SBP", units: "mmHg", modalities: ["ABP_Syst", "ART_Syst", "ART1_Syst", "ART2_Syst", "ART3_Syst", "ART4_Syst", "NBP_Syst"], goal: { lo: 100, hi: 160 }, precision: 0 },
	{ key: "DBP", label: "DBP", units: "mmHg", modalities: ["ABP_Dias", "ART_Dias", "ART1_Dias", "ART2_Dias", "ART3_Dias", "ART4_Dias", "NBP_Dias"], goal: { lo: 50, hi: 90 }, precision: 0 },
	{ key: "SpO2", label: "SpO2", units: "%", modalities: ["SpO2_na"], goal: { lo: 92, hi: null }, precision: 0 },
	{ key: "ICP", label: "ICP", units: "mmHg", modalities: ["ICP_Mean", "ICP1_Mean", "ICP2_Mean", "IC1_Mean", "IC2_Mean"], goal: { lo: null, hi: 22 }, precision: 0 },
	{ key: "CPP", label: "CPP", units: "mmHg", modalities: ["CPP_na"], goal: { lo: 60, hi: 70 }, precision: 0 },
	{ key: "EtCO2", label: "EtCO2", units: "mmHg", modalities: ["EtCO2_na"], goal: { lo: 35, hi: 45 }, precision: 0 },
	{ key: "Temp", label: "Temp", units: "°C", modalities: ["Tcore_na", "T1_na", "T2_na"], goal: { lo: 36, hi: 38 }, precision: 1 },
]

const ONE_MINUTE_MS = 60 * 1000
const ONE_HOUR_MS = 60 * ONE_MINUTE_MS

// Bin sizes offered for the % in goal strip: Data Review's window sizes from a minute up.
export const BIN_SIZE_OPTIONS: { label: string, value: number }[] = [
	{ label: "1 Min", value: ONE_MINUTE_MS },
	{ label: "5 Min", value: 5 * ONE_MINUTE_MS },
	{ label: "10 Min", value: 10 * ONE_MINUTE_MS },
	{ label: "15 Min", value: 15 * ONE_MINUTE_MS },
	{ label: "30 Min", value: 30 * ONE_MINUTE_MS },
	{ label: "1 Hr", value: ONE_HOUR_MS },
	{ label: "2 Hr", value: 2 * ONE_HOUR_MS },
	{ label: "4 Hr", value: 4 * ONE_HOUR_MS },
	{ label: "6 Hr", value: 6 * ONE_HOUR_MS },
	{ label: "8 Hr", value: 8 * ONE_HOUR_MS },
	{ label: "12 Hr", value: 12 * ONE_HOUR_MS },
	{ label: "16 Hr", value: 16 * ONE_HOUR_MS },
	{ label: "1 Day", value: 24 * ONE_HOUR_MS },
	{ label: "3 Days", value: 3 * 24 * ONE_HOUR_MS },
	{ label: "1 Week", value: 7 * 24 * ONE_HOUR_MS },
]

export const STRIP_BIN_MS = 5 * ONE_MINUTE_MS

// Where bins are counted from: the recording's start, or an hour of the day on its clock
// (07:00 gives day and night shifts with 12 Hr bins).
export const BIN_START_OPTIONS: { label: string, value: number | "recording" }[] = [
	{ label: "Start of recording", value: "recording" },
	...Array.from({ length: 24 }, (_, hour) => ({ label: `${String(hour).padStart(2, "0")}:00`, value: hour })),
]

const ONE_DAY_MINUTES = 24 * 60
const clockText = (minutes: number) => {
	const inDay = ((minutes % ONE_DAY_MINUTES) + ONE_DAY_MINUTES) % ONE_DAY_MINUTES
	return `${String(Math.floor(inDay / 60)).padStart(2, "0")}:${String(inDay % 60).padStart(2, "0")}`
}

/* The blocks a size and start give, in words, under the setting: "Blocks run 07:00 to 19:00,
   19:00 to 07:00." for 12 Hr from 07:00. Blocks restart each day, as query counts them. */
export function binStartText(binMs: number, binStart: TraceGoal["binStart"]): string {
	const size = BIN_SIZE_OPTIONS.find(option => option.value === binMs)?.label ?? `${Math.round(binMs / ONE_MINUTE_MS)} Min`
	if (binStart === "recording") {
		return `The first block starts when the recording starts, then a new one every ${size}.`
	}
	const start = (binStart ?? 0) * 60
	const binMinutes = binMs / ONE_MINUTE_MS
	if (binMinutes >= ONE_DAY_MINUTES) {
		return `Blocks start at ${clockText(start)} and each covers ${size}.`
	}
	const perDay = Math.ceil(ONE_DAY_MINUTES / binMinutes)
	const shown = Array.from({ length: Math.min(perDay, perDay > 3 ? 2 : 3) }, (_, i) =>
		`${clockText(start + i * binMinutes)} to ${clockText(Math.min(start + (i + 1) * binMinutes, start + ONE_DAY_MINUTES))}`)
	return `Blocks run ${shown.join(", ")}${perDay > 3 ? ", and so on" : ""}.`
}

// Analyses a goal can judge: query runs them and works out time in goal from their output.
const GOAL_ANALYTICS = new Set<string>([
	Analytic.PRx, Analytic.COx, Analytic.CPPOpt, Analytic.CPPOpt_FLEX, Analytic.MAPOpt, Analytic.MAPOpt_FLEX,
	Analytic.ROLLING_AVERAGE, Analytic.RESAMPLE,
])

/* A stored measurement or an analysis of one, drawn as a line: what a goal can be set on. */
export const canHaveGoal = (trace: any) => !!trace.rawDataKey
	&& trace.renderStrategy !== RenderStrategy.DELTA
	&& (!trace.onDemandAnalysis || GOAL_ANALYTICS.has(trace.onDemandAnalysis.analytic))

export function binText(periodMs: number | undefined): string {
	if (!periodMs) {
		return ""
	}
	const option = BIN_SIZE_OPTIONS.find(option => option.value === periodMs)
	return `${option?.label ?? `${Math.round(periodMs / ONE_MINUTE_MS)} Min`} bins`
}

export function vitalForModality(rawDataKey: string | undefined): GoalVital | undefined {
	return GOAL_VITALS.find(vital => rawDataKey !== undefined && vital.modalities.includes(rawDataKey))
}

export function directionOf(range: GoalRange): GoalDirection {
	if (range.lo !== null && range.hi !== null) {
		return "between"
	}
	return range.lo !== null ? "above" : "below"
}

export function rangeOf(goal: { direction: GoalDirection, lo: number | null, hi: number | null }): GoalRange {
	switch (goal.direction) {
		case "below": return { lo: null, hi: goal.hi }
		case "above": return { lo: goal.lo, hi: null }
		default: return { lo: goal.lo, hi: goal.hi }
	}
}

const isNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value)

/* What is wrong with a goal, in words, or undefined when it can be used. */
export function goalError(goal: { direction: GoalDirection, lo: number | null, hi: number | null }): string | undefined {
	switch (goal.direction) {
		case "below": return isNumber(goal.hi) ? undefined : "Enter the value it should stay below."
		case "above": return isNumber(goal.lo) ? undefined : "Enter the value it should stay above."
		default:
			if (!isNumber(goal.lo) || !isNumber(goal.hi)) {
				return "Enter both the low and the high value."
			}
			return goal.lo >= goal.hi ? "The low value must be below the high value." : undefined
	}
}

/* Every goal that is switched on but cannot be used, across all line graphs of a window, so the
   settings cannot be saved with one, whichever graph is on screen. */
export function windowGoalErrors(graphs: { renderStrategy?: string, traces?: any[] }[]): string[] {
	return graphs
		.filter(graph => graph.renderStrategy === RenderStrategy.LINE)
		.flatMap(graph => (graph.traces ?? []).flatMap(trace => {
			const error = trace.goal?.enabled ? goalError(trace.goal) : undefined
			return error ? [`${trace.name} goal: ${error}`] : []
		}))
}

export function newTraceGoal(rawDataKey: string | undefined, enabled: boolean = false): TraceGoal {
	const range = vitalForModality(rawDataKey)?.goal ?? { lo: null, hi: null }
	return {
		enabled,
		direction: range.lo === null && range.hi === null ? "between" : directionOf(range),
		lo: range.lo,
		hi: range.hi,
		lines: true,
		heatmap: true,
		shading: false,
		binMs: STRIP_BIN_MS,
	}
}

export function activeGoal(trace: any): TraceGoal | undefined {
	const goal: TraceGoal | undefined = trace?.goal
	return goal && goal.enabled && goalError(goal) === undefined ? goal : undefined
}

export function goalText(goal: GoalRange | undefined): string {
	if (!goal) {
		return ""
	}
	if (goal.lo !== null && goal.hi !== null) {
		return `${goal.lo} to ${goal.hi}`
	}
	if (goal.lo !== null) {
		return `> ${goal.lo}`
	}
	if (goal.hi !== null) {
		return `< ${goal.hi}`
	}
	return ""
}

// "MAP (% in goal 65 to 110, 5 Min bins)"
export function thresholdRowLabel(trace: { name: string, onDemandAnalysis?: any }): string {
	const analysis = trace.onDemandAnalysis as TimeInGoalAnalysis
	const range = { lo: analysis.goalLow ?? null, hi: analysis.goalHigh ?? null }
	const compact = range.lo !== null && range.hi !== null
		? `${range.lo} to ${range.hi}`
		: range.lo !== null ? `>${range.lo}` : `<${range.hi}`
	const bins = binText(analysis.calculationPeriodMs)
	return `${trace.name} (% in goal ${compact}${bins ? `, ${bins}` : ""})`
}

/* Percent of each bin the modality spent inside its goal, calculated by query. Only built from a
   usable goal, so at least one of goalLow and goalHigh is a number. timeZone is the recording's,
   so query can put bins of an hour or more on the local clock; binStart moves where they start. */
export function timeInGoalAnalysis(rawDataKey: string, goal: GoalRange, periodMs: number, timeZone?: string, binStart?: TraceGoal["binStart"], recordingStartMs?: number, source?: { analysis: OnDemandAnalysis, compositeIndex?: number }): TimeInGoalAnalysis {
	return {
		analytic: Analytic.TIME_IN_GOAL,
		rawDataKey,
		calculationPeriodMs: periodMs,
		calculationWindowMs: periodMs,
		goalLow: goal.lo,
		goalHigh: goal.hi,
		...(timeZone ? { timeZone } : {}),
		...(binStart === "recording" && recordingStartMs !== undefined ? { binAnchorMs: recordingStartMs } : {}),
		...(typeof binStart === "number" && binStart > 0 ? { binOffsetMinutes: binStart * 60 } : {}),
		...(source ? { source: source.analysis, ...(source.compositeIndex !== undefined ? { compositeIndex: source.compositeIndex } : {}) } : {}),
	}
}

// The legend label of a measurement's heatmap strip: its own id, so it hides the strip alone.
const GOAL_BAR_SUFFIX = "::goal-bar"
export const goalBarLabelId = (traceId: string) => `${traceId}${GOAL_BAR_SUFFIX}`
export const goalBarOwner = (labelId: string) => labelId.endsWith(GOAL_BAR_SUFFIX) ? labelId.slice(0, -GOAL_BAR_SUFFIX.length) : undefined

/* What strips are built from: the recording's zone and start, and whether a hidden strip keeps
   its row (only to tell which data a window needs, so hiding one does not reload it). */
export type StripContext = { timeZone?: string, recordingStartMs?: number, includeHidden?: boolean, patientModalities?: string[] }

export function thresholdRow(id: string, name: string, rawDataKey: string, analysis: TimeInGoalAnalysis, color: string): HeatmapTraceConfigJSON {
	return {
		id,
		name,
		rawDataKey,
		dataKey: `${rawDataKey}-${JSON.stringify(analysis)}`,
		dataSource: DataSource.CURRENT_PATIENT,
		units: "%",
		isCompositePart: false,
		isVisible: true,
		renderStrategy: RenderStrategy.HEATMAP,
		colorSpectrum: ColorSpectrum.RED_YELLOW_GREEN,
		lowerBound: 0,
		upperBound: 100,
		onDemandAnalysis: analysis,
		summaryValueConfig: { color, name: `${name} time in goal`, label: `${name} time in goal`, precision: 0, isVisible: true, isPercent: true, aggregate: "mean" },
	}
}

export const isThresholdRow = (trace: any) => trace?.onDemandAnalysis?.analytic === Analytic.TIME_IN_GOAL

// Height of the label lane above each goal row (a 12px label plus a little air).
export const THRESHOLD_LABEL_LANE_PX = 18

export const hasLabelLanes = (traces: any[]) => traces.length > 0 && traces.every(isThresholdRow)

const THRESHOLD_GRAPH_SETTINGS = { min: 0, max: 100, defaultMin: 0, defaultMax: 100, autoScale: false }

// Heatmap strips: a thin % in goal row pressed under a line graph for each shown measurement whose
// goal has "heatmap" on. In the saved display it is that one setting; the window draws the strip
// as a heatmap graph of its own. The measurement's legend label hides only its values; the row's
// own legend label hides only the row.
const STRIP_HEIGHT = 0.16 // of the line graph's height, per row
const BAR_ONLY_STRIP_HEIGHT = 0.4 // with the chart gone, enough for its value and time range

// The labels row left of a line graph when only its bar shows.
export const BAR_ONLY_LABELS_PX = 20

const goalSources = (graph: any) => (graph.traces as any[]).filter(canHaveGoal)

export function stripRows(graph: any, context: StripContext = {}): HeatmapTraceConfigJSON[] {
	if (graph.renderStrategy !== RenderStrategy.LINE) {
		return []
	}
	return goalSources(graph).flatMap(trace => {
		const goal = activeGoal(trace)
		if (!goal || !goal.heatmap || (goal.barHidden && !context.includeHidden)) {
			return []
		}
		const label = vitalForModality(trace.rawDataKey)?.label ?? trace.name
		const source = trace.onDemandAnalysis ? { analysis: trace.onDemandAnalysis, compositeIndex: trace.isCompositePart ? trace.compositeIndex : undefined } : undefined
		const analysis = timeInGoalAnalysis(trace.rawDataKey, rangeOf(goal), goal.binMs, context.timeZone, goal.binStart, context.recordingStartMs, source)
		const row = thresholdRow(`${graph.id}-${trace.id}-goal-strip`, label, trace.rawDataKey, analysis, trace.color)
		return [{ ...row, dataObjectId: trace.dataObjectId, toggleTarget: { graphId: graph.id, traceId: trace.id } } as unknown as HeatmapTraceConfigJSON]
	})
}

/* The graphs as saved: strips dropped, and with them the link from a measurement to its strip. */
export function collapseGoalStrips<T>(graphs: T[]): T[] {
	return (graphs as any[])
		.filter(graph => !graph.isGoalStrip)
		.map(graph => {
			if (!("barOnly" in graph) && !(graph.traces ?? []).some((trace: any) => trace.goalPercent)) {
				return graph
			}
			const { barOnly: _, barValueRows: _rows, ...rest } = graph
			return { ...rest, traces: graph.traces.map(({ goalPercent: _percent, ...trace }: any) => trace) }
		}) as T[]
}

/* The graphs as the window draws them: each line graph followed by its strip, the chart shrunk to
   its labels when every measurement is hidden. A row's % in goal shows under its measurement's
   value, or on its own in the graph's value column when that value is turned off. Safe to run
   on its own output. */
export function expandGoalStrips<T>(graphs: T[], context: StripContext = {}): T[] {
	return (collapseGoalStrips(graphs) as any[])
		.flatMap(graph => {
			const rows = stripRows(graph, context)
			if (rows.length === 0) {
				return [graph]
			}
			const rowFor = (traceId: string) => rows.find((row: any) => row.toggleTarget.traceId === traceId)
			const valueShown = (traceId: string) => !!graph.traces.find((trace: any) => trace.id === traceId)?.summaryValueConfig?.isVisible
			const traces = graph.traces.map((trace: any) => {
				const row = rowFor(trace.id)
				return row && valueShown(trace.id)
					? { ...trace, goalPercent: { dataKey: row.dataKey, binMs: (row.onDemandAnalysis as TimeInGoalAnalysis).calculationPeriodMs, timeZone: (row.onDemandAnalysis as TimeInGoalAnalysis).timeZone } }
					: trace
			})
			const hidden = (trace: any) => !(trace.isVisible ?? true)
			// A measurement this recording does not have cannot be clicked off, so it does not hold the chart open.
			const absent = (trace: any) => context.patientModalities !== undefined && !trace.onDemandAnalysis && !context.patientModalities.includes(trace.rawDataKey)
			const barOnly = graph.traces.some(hidden) && graph.traces.every((trace: any) => hidden(trace) || absent(trace))
			const strip = {
				...graph,
				id: `${graph.id}-goal-strip`,
				name: `${graph.name} % in goal`,
				renderStrategy: RenderStrategy.HEATMAP,
				...THRESHOLD_GRAPH_SETTINGS,
				heightRatio: (graph.heightRatio ?? 1) * (barOnly ? BAR_ONLY_STRIP_HEIGHT : STRIP_HEIGHT) * rows.length,
				summaryValueColumns: 1,
				isGoalStrip: true,
				traces: rows.map((row: any) => ({ ...row, summaryValueConfig: undefined })),
			}
			const barValueRows = rows.filter((row: any) => !valueShown(row.toggleTarget.traceId)).map((row: any) => ({ ...row, color: row.summaryValueConfig.color }))
			return [{ ...graph, traces, barOnly, barValueRows }, strip]
		}) as T[]
}

// Where each block ends: at its size, or sooner when the next block starts first (blocks restart each day).
function binEnds(series: { times: ArrayLike<number> }[], binMs: number): Map<number, number> {
	const starts = [...new Set(series.flatMap(({ times }) => Array.from(times)))].sort((a, b) => a - b)
	return new Map(starts.map((start, i) => [start, Math.min(start + binMs, starts[i + 1] ?? Infinity)]))
}

/* The block holding this time. Undefined when no block is there. */
export function binSpan(series: { times: ArrayLike<number> }[], time: number, binMs: number): [number, number] | undefined {
	for (const [start, end] of binEnds(series, binMs)) {
		if (start <= time && time < end) {
			return [start, end]
		}
	}
	return undefined
}

const SECONDS_UNDER_MS = 10 * 60_000
const spanFormatters = new Map<string, Intl.DateTimeFormat>()

/* The two lines naming a span of time, in the recording's zone. A short span not on whole minutes shows seconds. */
export function spanLines(start: number, end: number, timeZone?: string): [string, string] {
	const seconds = end - start < SECONDS_UNDER_MS && (start % 60_000 !== 0 || end % 60_000 !== 0)
	const key = `${timeZone ?? ""}|${seconds}`
	let formatter = spanFormatters.get(key)
	if (!formatter) {
		formatter = new Intl.DateTimeFormat("en-US", { timeZone: timeZone || undefined, month: "short", day: "numeric", hour: "numeric", minute: "2-digit", second: seconds ? "2-digit" : undefined, hour12: true })
		spanFormatters.set(key, formatter)
	}
	return [formatter.format(new Date(start)), `to ${formatter.format(new Date(end))}`]
}

/* The mean of the bins from start to end, each weighted by its time in that span and counted
   once by its start even when two pages both hold it; start equal to end gives the bin at that
   time. Undefined when no bin is there. */
export function meanOfBins(series: { times: ArrayLike<number>, values: ArrayLike<number> }[], start: number, end: number, binMs: number): number | undefined {
	const bins = new Map<number, { value: number, weight: number }>()
	const ends = binEnds(series, binMs)
	series.forEach(({ times, values }) => {
		for (let i = 0; i < times.length; i++) {
			const binEnd = ends.get(times[i]) ?? times[i] + binMs
			const weight = start === end ? (times[i] <= start && start < binEnd ? 1 : 0) : Math.min(binEnd, end) - Math.max(times[i], start)
			if (weight > 0 && Number.isFinite(values[i])) {
				bins.set(times[i], { value: values[i], weight })
			}
		}
	})
	let sum = 0
	let total = 0
	bins.forEach(({ value, weight }) => {
		sum += value * weight
		total += weight
	})
	return total > 0 ? sum / total : undefined
}

/* Fixed y limits widened to show a goal's values with a little room, or undefined when they
   already do. Rounded outward so the limits stay easy to read. */
export function limitsShowingGoal(min: number, max: number, goal: GoalRange): { min: number, max: number } | undefined {
	const values = goalLines(goal).map(line => line.value)
	const lo = Math.min(min, ...values)
	const hi = Math.max(max, ...values)
	if (lo === min && hi === max) {
		return undefined
	}
	const pad = 0.05 * (hi - lo || 1)
	const round = (value: number, toward: (x: number) => number) => hi - lo > 5 ? toward(value) : toward(value * 100) / 100
	return { min: lo < min ? round(lo - pad, Math.floor) : min, max: hi > max ? round(hi + pad, Math.ceil) : max }
}

/* The graphs as saved from the window's settings: a line graph with fixed y limits that leave a
   goal shown or changed in this edit off screen gets limits wide enough for it, so limits a user
   narrows later stay. Autoscaled graphs already include goals. */
export function withGoalsInView<T>(graphs: T[], before: T[] = []): T[] {
	const shownGoal = (trace: any) => {
		const goal = activeGoal(trace)
		return goal && (goal.lines || goal.shading) ? goal : undefined
	}
	const goalKey = (trace: any) => {
		const goal = shownGoal(trace)
		return goal ? JSON.stringify(rangeOf(goal)) : ""
	}
	return (graphs as any[]).map(graph => {
		if (graph.renderStrategy !== RenderStrategy.LINE || graph.autoScale) {
			return graph
		}
		const previous = (before as any[]).find(other => other.id === graph.id)
		let min = graph.defaultMin ?? graph.min
		let max = graph.defaultMax ?? graph.max
		;(graph.traces ?? []).forEach((trace: any) => {
			const old = previous?.traces?.find((other: any) => other.id === trace.id)
			const goal = old && goalKey(old) === goalKey(trace) ? undefined : shownGoal(trace)
			const limits = goal ? limitsShowingGoal(min, max, rangeOf(goal)) : undefined
			if (limits) {
				min = limits.min
				max = limits.max
			}
		})
		return min === (graph.defaultMin ?? graph.min) && max === (graph.defaultMax ?? graph.max)
			? graph
			: { ...graph, min, max, defaultMin: min, defaultMax: max }
	}) as T[]
}

export function goalLines(goal: GoalRange | undefined): { value: number, color: string, isDashed: boolean }[] {
	return [goal?.lo, goal?.hi]
		.filter((value): value is number => typeof value === "number" && Number.isFinite(value))
		.map(value => ({ value, color: "var(--color-chart-axis-text)", isDashed: true }))
}
